import DropdownSelect from "../../../../common/DropdownSelect";

import { useFormikContext } from "formik";
import { Box, IconButton, SelectChangeEvent, Tooltip } from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { startCase } from "lodash";
import { actionOptions, ecosystemOptions } from "./inputOptions";
import { AddSolutionFormValues } from "../../../add/form/types";
import { EditSolutionFormValues } from "../../../edit/form/types";
import type {
  InterventionErrorItem,
  InterventionFieldConfig,
  InterventionFieldName,
  InterventionInputProps,
  InterventionTouchedItem,
} from "../../types";

function Intervention({ value, index, remove }: InterventionInputProps) {
  const context = useFormikContext<
    AddSolutionFormValues | EditSolutionFormValues
  >();

  const interventionFields: Array<InterventionFieldConfig> = [
    {
      fieldName: "action",
      options: actionOptions,
    },
    {
      fieldName: "ecosystem",
      options: ecosystemOptions,
    },
  ];

  const ensureStringArray = (value: unknown): Array<string> => {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === "string");
  };

  const getValueFromFormState = (
    fieldName: InterventionFieldName,
  ): Array<string> => {
    const intervention = context.values["interventions"][index];
    const field = ensureStringArray(intervention?.[fieldName]);
    return field;
  };

  const setValueAsFormState = (
    event: SelectChangeEvent<string | number | Array<string>>,
    fieldName: InterventionFieldName,
  ) => {
    const selectedValues = Array.isArray(event.target.value)
      ? event.target.value.map(String)
      : [String(event.target.value)];
    const path = `interventions[${index}].${fieldName}`;
    context.setFieldTouched(path, true, false);
    context.setFieldValue(path, selectedValues, false);
  };

  const getErrorFromFormState = (
    fieldName: InterventionFieldName,
  ): string | undefined => {
    const touchedRow = (
      context.touched?.interventions as
        Array<InterventionTouchedItem | undefined> | undefined
    )?.[index];
    const errorsRow = (
      context.errors?.interventions as
        Array<InterventionErrorItem | undefined> | undefined
    )?.[index];

    const pairTouched =
      Boolean(touchedRow?.action) || Boolean(touchedRow?.ecosystem);
    if (!pairTouched) return undefined;

    const fieldError = errorsRow?.[fieldName];
    if (!fieldError) return undefined;
    if (typeof fieldError === "string") return fieldError;
    if (typeof fieldError === "object") return fieldError.message;
    return undefined;
  };

  const markTouched = (fieldName: InterventionFieldName) => {
    context.setFieldTouched(`interventions[${index}].${fieldName}`, true, true);
  };

  return (
    <Box
      data-testid={`solution-intervention-container-${value.interventionCounter}`}
      sx={{
        display: "flex",
        direction: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        data-testid={`solution-intervention-container-dropdown-fields-${value.interventionCounter}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "90%",
        }}
      >
        {interventionFields.map((intervention) => {
          const validationMessage = getErrorFromFormState(
            intervention.fieldName,
          );
          return (
            <DropdownSelect
              key={intervention.fieldName}
              options={intervention.options}
              value={getValueFromFormState(intervention.fieldName)}
              onChange={(
                event: SelectChangeEvent<string | number | string[]>,
              ) => setValueAsFormState(event, intervention.fieldName)}
              label={startCase(intervention.fieldName)}
              onBlur={() => markTouched(intervention.fieldName)}
              id={`solution-intervention-${intervention.fieldName}-${value.interventionCounter}`}
              multiple
              error={Boolean(validationMessage)}
              helperText={validationMessage}
              name={`interventions[${index}].${intervention.fieldName}`}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Tooltip title="Delete intervention">
          <IconButton
            onClick={remove}
            sx={{
              padding: 0,
            }}
            aria-label={`delete-intervention-${value.interventionCounter}`}
            size="large"
          >
            <DeleteForeverIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default Intervention;
